"use client";
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { BASE_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const CanvaContext = createContext();
export const useCanva = () => useContext(CanvaContext);

/**
 * 🔁 Backend → Frontend normalizer
 */
const normalizeCanva = (c) => ({
  id: c?.id ? String(c.id) : "",
  type: c?.type ?? "",
  status: c?.status ? c.status.toLowerCase() : "inactive",
  deliveryRate: Number(c?.delivery_rate ?? 0),
  messagesSent: 0,
});


export const CanvaProvider = ({ children }) => {
  const { token, logout } = useAuth();

  const [canvaCampaigns, setCanvaCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

 const CANVA_ENDPOINT = `${BASE_URL}/canvas`;


  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  // 🔹 FETCH
const fetchCanvaCampaigns = useCallback(async () => {
  if (!token) return;

  setLoading(true);
  setError(null);

  try {
    const res = await fetch(CANVA_ENDPOINT, { headers });

    if (!res.ok) {
      throw new Error("API failed");
    }

    const json = await res.json();

    let list = [];

    if (Array.isArray(json)) {
      list = json;
    } else if (Array.isArray(json.data)) {
      list = json.data;
    } else if (json && typeof json === "object" && json.id) {
      list = [json]; // ✅ YOUR API CASE
    }

    setCanvaCampaigns(list.map(normalizeCanva));
  } catch (err) {
    // console.error(err);
    setCanvaCampaigns([]);   // ✅ IMPORTANT
    setError("Failed to load Canva campaigns");
  } finally {
    setLoading(false);
  }
}, [token, logout]);


  // 🔹 ADD
  const addCanva = async (payload) => {
    const res = await fetch(CANVA_ENDPOINT, {
      method: "POST",
      headers,
      body: JSON.stringify({
        type: payload.type,
        status: payload.status === "active" ? "Active" : "Inactive",
        delivery_rate: payload.deliveryRate,
      }),
    });

    const json = await res.json();
    const created = normalizeCanva(json.data ?? json);

    setCanvaCampaigns((prev) => [...prev, created]);
  };

  // 🔹 UPDATE
  const updateCanva = async (payload) => {
    const res = await fetch(`${CANVA_ENDPOINT}/${payload.id}`, {
      method: "PUT",
      headers,
      body: JSON.stringify({
        type: payload.type,
        status: payload.status === "active" ? "Active" : "Inactive",
        delivery_rate: payload.deliveryRate,
      }),
    });

    const json = await res.json();
    const updated = normalizeCanva(json.data ?? json);

    setCanvaCampaigns((prev) =>
      prev.map((c) => (c.id === updated.id ? updated : c))
    );
  };

  // 🔹 DELETE
  const deleteCanva = async (id) => {
    await fetch(`${CANVA_ENDPOINT}/${id}`, {
      method: "DELETE",
      headers,
    });

    setCanvaCampaigns((prev) =>
      prev.filter((c) => c.id !== String(id))
    );
  };

  // 🔹 TOGGLE STATUS
  const toggleCanvaStatus = async (item) => {
    await updateCanva({
      ...item,
      status: item.status === "active" ? "inactive" : "active",
    });
  };

  useEffect(() => {
    fetchCanvaCampaigns();
  }, [fetchCanvaCampaigns]);

  return (
    <CanvaContext.Provider
      value={{
        canvaCampaigns,
        loading,
        error,
        addCanva,
        updateCanva,
        deleteCanva,
        toggleCanvaStatus,
        refreshCanva: fetchCanvaCampaigns,
      }}
    >
      {children}
    </CanvaContext.Provider>
  );
};
